import React, { useEffect, useRef, useState } from "react";

/**
 * Draw-to-sign pad with typed-name fallback + explicit consent checkbox.
 * onSign receives { imageBase64, name, method } — caller adds timestamp/hash
 * and POSTs to the backend. No local persistence: the backend record is truth.
 */
export default function SignaturePad({ defaultName, onSign, signing, error }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [typedName, setTypedName] = useState(defaultName || "");
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#1a1a2e";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  function pos(e) {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: ((t.clientX - r.left) / r.width) * c.width, y: ((t.clientY - r.top) / r.height) * c.height };
  }
  function start(e) {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  }
  function move(e) {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  }
  function end() { drawing.current = false; }
  function clear() {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, c.width, c.height);
    setHasInk(false);
  }

  const canSign = agreed && !!typedName.trim() && !signing;

  function submit() {
    if (!canSign) return;
    let imageBase64;
    if (hasInk) {
      imageBase64 = canvasRef.current.toDataURL("image/png");
    } else {
      // typed-name fallback: render the plain typed name onto the pad (no cursive fakery)
      const c = document.createElement("canvas");
      c.width = 400; c.height = 120;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
      ctx.fillStyle = "#1a1a2e"; ctx.font = "28px Arial";
      ctx.textBaseline = "middle";
      ctx.fillText(typedName.trim(), 20, 60);
      imageBase64 = c.toDataURL("image/png");
    }
    onSign({ imageBase64, name: typedName.trim(), method: hasInk ? "drawn" : "typed" });
  }

  return (
    <div style={{ border: "1px solid #E0E0E0", borderRadius: 10, padding: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#00B8D4", marginBottom: 6 }}>SIGN BELOW</div>
      <canvas
        ref={canvasRef}
        width={400}
        height={120}
        style={{ width: "100%", height: 120, border: "1px dashed #bbb", borderRadius: 6, touchAction: "none", background: "#fff", cursor: "crosshair" }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
        <span style={{ fontSize: 11, color: "#888" }}>{hasInk ? "Signature captured" : "Draw your signature, or sign with your typed name"}</span>
        <button type="button" onClick={clear} style={{ border: "none", background: "transparent", color: "#00B8D4", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>Clear</button>
      </div>
      <input
        type="text"
        value={typedName}
        onChange={(e) => setTypedName(e.target.value)}
        placeholder="Your full legal name"
        style={{ width: "100%", boxSizing: "border-box", border: "1px solid #E0E0E0", borderRadius: 6, padding: 8, marginTop: 8, fontSize: 13 }}
      />
      <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 10, fontSize: 13, cursor: "pointer" }}>
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ marginTop: 2 }} />
        <span>I agree to the above terms</span>
      </label>
      {error && <div style={{ color: "#c00", fontSize: 12, marginTop: 8 }}>{error}</div>}
      <button
        type="button"
        disabled={!canSign}
        onClick={submit}
        style={{ width: "100%", marginTop: 10, padding: "10px 0", border: "none", borderRadius: 6, background: canSign ? "#00B8D4" : "#ccc", color: "#fff", fontWeight: 600, cursor: canSign ? "pointer" : "default" }}>
        {signing ? "Signing…" : "Sign Agreement"}
      </button>
    </div>
  );
}
